// Side-effect: patches window.fetch with the 401→refresh interceptor at module-evaluation time,
// before any React render or useEffect. Must be the first import in this file.
import "@/lib/api/auto-install-interceptor";
import "@/lib/pwa/install-prompt";

import { useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { MapPinOff, AlertTriangle } from "lucide-react";

import { absoluteUrl } from "@/lib/seo/site-url";
import appCss from "../styles.css?url";
import faviconUrl from "@/assets/placeholder-logo.png";
import { AuthProvider } from "@/hooks/use-auth";
import { ImpersonationBanner } from "@/components/auth/ImpersonationBanner";
import { BackToTop } from "@/components/back-to-top";
import { Toaster } from "@/components/ui/sonner";

const HELPFUL_LINKS = [
  { label: "Home", to: "/home" as const },
  { label: "Log in", to: "/login" as const },
];

export function NotFoundComponent() {
  const router = useRouter();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <img src={faviconUrl} alt="Dev-Hub" className="mx-auto h-10 w-auto object-contain" />
        <MapPinOff className="mx-auto mt-6 h-16 w-16 text-muted-foreground/40" aria-hidden="true" />
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-4 rounded-sm text-xl font-semibold text-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none"
        >
          Page not found
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => router.history.back()}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go back
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
        <div className="mt-8 border-t pt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Helpful pages
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
            {HELPFUL_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-primary underline-offset-4 hover:underline"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <img src={faviconUrl} alt="Dev-Hub" className="mx-auto h-10 w-auto object-contain" />
        <AlertTriangle className="mx-auto mt-6 h-16 w-16 text-destructive/60" aria-hidden="true" />
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-4 rounded-sm text-xl font-semibold tracking-tight text-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none"
        >
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Dev-Hub" },
      { title: "dev-hub — Training & Communication Dashboard" },
      {
        name: "description",
        content: "Secure onboarding, training, and communication platform.",
      },
      { property: "og:title", content: "dev-hub — Training & Communication Dashboard" },
      {
        property: "og:description",
        content: "Secure onboarding, training, and communication platform.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@devhub" },
      { name: "twitter:title", content: "dev-hub — Training & Communication Dashboard" },
      {
        name: "twitter:description",
        content: "Training & communication dashboard for your team.",
      },
      { name: "theme-color", content: "#163569" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "DevHub" },
      { property: "og:site_name", content: "Dev-Hub" },
      { property: "og:url", content: absoluteUrl("/") },
      { name: "robots", content: "index, follow" },
    ],
    links: [
      {
        rel: "icon",
        href: faviconUrl,
      },
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "manifest",
        href: "/manifest.webmanifest",
      },
      {
        rel: "apple-touch-icon",
        href: "/icons/apple-touch-icon.png",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* Above Outlet so they pin to the top of every page and survive navigation. */}
        <ImpersonationBanner />
        <Outlet />
        {/* Mounted here rather than per-layout so it covers the public routes too; it stays hidden
            until the page is actually scrolled, so short pages are unaffected. */}
        <BackToTop />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
